/*
 * KANBAN FILTER UTILITIES
 * 
 * Functions for applying filters to Kanban board data:
 * - applyFilters: Main function to filter data based on FilterGroup
 * - evaluateRule: Evaluate a single filter rule against an item
 * - buildFilterSummary: Generate human-readable filter description
 * 
 * Supports special values like "Me" for owner fields.
 */

import { FilterGroup, FilterRule, OPERATOR_LABELS } from '../models/kanban';

/**
 * Apply filters to data array
 * @param data Array of items to filter
 * @param filters FilterGroup with rules and nested groups
 * @param currentUserId Current user ID for "Me" resolution
 * @returns Filtered array
 */
export function applyFilters(
  data: any[],
  filters: FilterGroup,
  currentUserId: string
): any[] {
  if (!filters || (filters.rules.length === 0 && filters.groups.length === 0)) {
    return data;
  }

  return data.filter(item => evaluateGroup(item, filters, currentUserId));
}

/**
 * Evaluate a filter group against an item
 */
function evaluateGroup(
  item: any,
  group: FilterGroup,
  currentUserId: string
): boolean {
  const ruleResults = group.rules.map(rule => evaluateRule(item, rule, currentUserId));
  const groupResults = group.groups.map(g => evaluateGroup(item, g, currentUserId));
  const allResults = [...ruleResults, ...groupResults];

  if (allResults.length === 0) return true;

  if (group.operator === 'AND') {
    return allResults.every(Boolean);
  } else {
    return allResults.some(Boolean);
  }
}

/**
 * Evaluate a single filter rule against an item
 */
export function evaluateRule(
  item: any,
  rule: FilterRule,
  currentUserId: string
): boolean {
  if (!rule.field || !rule.operator) return true;

  const itemValue = item[rule.field];
  let ruleValue = rule.value;

  // Handle special "Me" value for owner fields
  if (ruleValue === '__ME__' || ruleValue === 'Me') {
    ruleValue = currentUserId;
  }

  // Handle array values for is_any_of
  if (Array.isArray(ruleValue)) {
    if (rule.operator === 'is_any_of') {
      const normalizedRuleValues = ruleValue.map(v =>
        v === '__ME__' || v === 'Me' ? currentUserId : String(v).toLowerCase()
      );
      const normalizedItemValue = String(itemValue || '').toLowerCase();
      return normalizedRuleValues.includes(normalizedItemValue);
    }
  }

  const normalizedItemValue = String(itemValue || '').toLowerCase();
  const normalizedRuleValue = String(ruleValue).toLowerCase();

  switch (rule.operator) {
    case 'is':
      return normalizedItemValue === normalizedRuleValue;

    case 'is_not':
      return normalizedItemValue !== normalizedRuleValue;

    case 'contains':
      return normalizedItemValue.includes(normalizedRuleValue);

    case 'starts_with':
      return normalizedItemValue.startsWith(normalizedRuleValue);

    case 'ends_with':
      return normalizedItemValue.endsWith(normalizedRuleValue);

    case 'equals':
      return Number(itemValue) === Number(ruleValue);

    case 'greater_than':
      return Number(itemValue) > Number(ruleValue);

    case 'less_than':
      return Number(itemValue) < Number(ruleValue);

    case 'before':
      return new Date(itemValue) < new Date(ruleValue as string);

    case 'after':
      return new Date(itemValue) > new Date(ruleValue as string);

    case 'is_any_of':
      // Already handled above for arrays
      return normalizedItemValue === normalizedRuleValue;

    default:
      return true;
  }
}

/**
 * Build a human-readable summary of filters
 * @param filters FilterGroup to summarize
 * @param fieldLabels Optional map of field names to labels
 * @returns Summary string like "Where: Owner is Me AND City is Toronto"
 */
export function buildFilterSummary(
  filters: FilterGroup,
  fieldLabels?: Record<string, string>
): string {
  if (!filters || (filters.rules.length === 0 && filters.groups.length === 0)) {
    return '';
  }

  const parts: string[] = [];

  for (const rule of filters.rules) {
    const fieldLabel = fieldLabels?.[rule.field] || rule.field;
    const operatorLabel = OPERATOR_LABELS[rule.operator] || rule.operator;

    let valueDisplay: string;
    if (Array.isArray(rule.value)) {
      valueDisplay = rule.value.join(', ');
    } else if (rule.value === '__ME__') {
      valueDisplay = 'Me';
    } else {
      valueDisplay = String(rule.value);
    }

    parts.push(`${fieldLabel} ${operatorLabel} ${valueDisplay}`);
  }

  // Handle nested groups recursively
  for (const group of filters.groups) {
    const groupSummary = buildFilterSummary(group, fieldLabels);
    if (groupSummary) {
      parts.push(`(${groupSummary.replace('Where: ', '')})`);
    }
  }

  if (parts.length === 0) return '';

  const separator = ` ${filters.operator} `;
  return `Where: ${parts.join(separator)}`;
}

/**
 * Check if a FilterGroup has any rules
 */
export function hasFilters(filters: FilterGroup): boolean {
  if (!filters) return false;
  if (filters.rules.length > 0) return true;
  return filters.groups.some(g => hasFilters(g));
}

/**
 * Count total number of filter rules (including nested)
 */
export function countFilterRules(filters: FilterGroup): number {
  if (!filters) return 0;
  let count = filters.rules.length;
  for (const group of filters.groups) {
    count += countFilterRules(group);
  }
  return count;
}
